import { RotateCcw } from 'lucide-react';
import type { ConfigurationReleaseDto } from '@ederest/contracts';
import { Badge } from '../../shared/ui/Badge.js';
import { Button } from '../../shared/ui/Button.js';
import { Table, type Column } from '../../shared/ui/Table.js';

interface ReleaseTableProps {
  releases: readonly ConfigurationReleaseDto[];
  latestVersion: number;
  busy: boolean;
  onActivate: (version: number) => void;
}

const DATE_FORMAT = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });

/**
 * Every published version, newest first (functional §5.7). Reactivating an
 * older one is the rollback; the newest keeps its number, so the table also
 * tells that the served version is behind.
 */
export function ReleaseTable({ releases, latestVersion, busy, onActivate }: ReleaseTableProps) {
  const columns: Array<Column<ConfigurationReleaseDto>> = [
    {
      key: 'version',
      header: 'Version',
      width: '120px',
      render: (row) => (
        <span className="script__version">
          <strong>v{row.version}</strong>
          {row.isActive && <Badge tone="positive">en ligne</Badge>}
          {row.isActive && row.version < latestVersion && <Badge tone="warning">retour arrière</Badge>}
        </span>
      ),
    },
    { key: 'guidances', header: 'Aides incluses', render: (row) => `${row.guidanceCount}` },
    {
      key: 'publishedAt',
      header: 'Publiée le',
      render: (row) => DATE_FORMAT.format(new Date(row.publishedAt)),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (row) =>
        row.isActive ? null : (
          <Button
            variant="ghost"
            icon={<RotateCcw size={13} />}
            disabled={busy}
            onClick={() => {
              if (
                window.confirm(
                  `Remettre la version v${row.version} en ligne ? Le portail l’affichera dans la minute, à la place de la version actuelle.`,
                )
              ) {
                onActivate(row.version);
              }
            }}
          >
            Remettre en ligne
          </Button>
        ),
    },
  ];

  return (
    <Table
      columns={columns}
      rows={releases}
      rowKey={(row) => String(row.version)}
      empty="Aucune version mise en ligne. Publiez d'abord des aides validées, puis mettez une version en ligne."
    />
  );
}
